import { type GetStaticProps, type InferGetStaticPropsType } from "next";
import { type ParsedUrlQuery } from "querystring";
import { PortableText } from "@portabletext/react";
import Footer from "~/components/Footer";
import PodHead from "~/components/Head";
import Navbar from "~/components/Navbar";
import PlayerEmbed from "~/components/Podcast/PlayerEmbed";
import { api } from "~/utils/api";
import { urlFor } from "~/utils/sanity.utils";
import { type DehydratedState } from "@tanstack/react-query";
import {
  dehydrateState,
  serversideHelpers,
} from "~/utils/serverside_helper.util";
import AppConstants from "~/constants/app.constants";

export const getStaticPaths = async () => {
  const paths = await serversideHelpers.content.getEpisodeSlugs.fetch();

  return {
    paths,
    fallback: true,
  };
};

type EpisodeParams = ParsedUrlQuery & {
  slug: string;
};

export const getStaticProps: GetStaticProps<{
  trpcState: DehydratedState;
  slug: string;
}> = async ({ params }) => {
  const { slug } = params as EpisodeParams;
  if (!slug) {
    return {
      notFound: true,
    };
  }

  await serversideHelpers.content.getEpisode.prefetch({ slug });

  return {
    props: {
      trpcState: dehydrateState(serversideHelpers.dehydrate),
      slug,
    },
    revalidate: AppConstants.DEFAULT_REVALIDATE_IN_SECONDS,
  };
};

const EpisodePage = ({
  slug,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { data: episode } = api.content.getEpisode.useQuery(
    {
      slug: slug,
    },
    { refetchOnMount: false, refetchOnWindowFocus: false }
  );

  const portableTextComponents = {
    types: {
      image: ({ value }: { value: string }) => (
        <img
          src={urlFor(value).url()}
          className="my-4 rounded-lg"
          alt="Image"
        />
      ),
    },
  };

  return (
    <>
      <PodHead title={episode?.name || "Loading episode"} />
      <main className="flex min-h-screen flex-col items-center justify-between bg-gradient-to-t from-orange-400 via-orange-300 to-indigo-500">
        <div className="fixed left-0 right-0 top-0 z-10 w-full">
          <Navbar showLogo />
        </div>
        <div className="flex h-full w-full flex-1 flex-col items-center bg-base-100/50 backdrop-blur-sm">
          <div className="w-full">
            {episode ? (
              <>
                <div
                  className="hero min-h-[70vh]"
                  style={{
                    backgroundImage: `url(${episode?.images[0]?.url || ""})`,
                  }}
                >
                  <div className="hero-overlay bg-opacity-60"></div>
                  <div className="hero-content w-full text-center text-neutral-content">
                    <div className="mt-8 w-full rounded-xl bg-base-100/50 p-4 shadow-xl backdrop-blur-sm">
                      <h1 className="mb-5 text-5xl font-bold text-base-content">
                        {episode.name}
                      </h1>
                      <PlayerEmbed episodeId={episode?.spotifyId} noLoad />
                    </div>
                  </div>
                </div>
                <div className="flex w-full items-center justify-center">
                  <article className="">
                    <div className="mx-4 mb-16 mt-10 min-h-[30vh] max-w-prose md:mx-0">
                      <PortableText
                        value={episode.content}
                        components={portableTextComponents}
                      />
                    </div>
                  </article>
                </div>
              </>
            ) : (
              <div>Episode not found</div>
            )}
          </div>
        </div>
        <div className="w-full transition-opacity">
          <Footer />
        </div>
      </main>
    </>
  );
};

export default EpisodePage;
